// Canonical state, rendered honestly. One table, used everywhere.
//
// Spec ch.06, on the production states the visual simulation does not have:
//
//   "Keep the same cards, workbench and drawers while adding clear
//    badges/actions for: queued, submitting, confirmed, proven_not_applied,
//    outcome_unknown, reauthorisation_required, stale_revision and unsupported
//    capabilities. Show an unknown outcome as requiring a status check, not as
//    a failed booking eligible for blind retry."
//
//   "The historical demo confirms some approved bookings/messages immediately.
//    That is simulated timing only. The production API returns an operation ID,
//    waits for provider evidence and displays the actual outcome. A confirmed
//    provider draft must not be labelled final posting."
//
// So this file has one hard rule, and there is a test that enforces it over the
// whole table rather than over the entries someone remembered to check: NO
// label may say booked, posted, or any other word that claims the money moved,
// unless the state is `confirmed` — and even `confirmed` says what the receipt
// says, which for a provider draft is a draft.
//
// The labels are English presentation of values that stay canonical on the
// wire. contracts/http-api.md: "Localise presentation without changing
// canonical enum values."

import { useMemo } from 'react';
import type { Booking } from './api/types.ts';
import { en, type MessageKey } from './i18n/en.ts';
import { useT, type Translate } from './i18n/index.tsx';

export type Tone = 'neutral' | 'ok' | 'warn' | 'danger' | 'ai' | 'blue';

export interface StateDisplay {
  readonly label: string;
  readonly tone: Tone;
  /** One sentence a reviewer can act on. Never a reassurance. */
  readonly explanation: string;
  /** True only where the external effect is evidenced by a receipt. */
  readonly effectConfirmed: boolean;
  /** What the reviewer may do next, if anything. */
  readonly nextStep?: 'check_status' | 'authorise_retry' | 'review';
  /** For a narrow row (the inbox). The same state, fewer words; never a different claim. */
  readonly short?: string;
}

/**
 * app.operation_state, from migration 0005.
 *
 * `unknown` is the one to read twice. The worker reaches it when a write may or
 * may not have been applied, and the whole payment guarantee rests on nobody —
 * machine or human — treating it as a failure and sending again. So its label
 * says what is true (we do not know) and its next step is a status check, never
 * a retry.
 */
export const OPERATION_STATES: Readonly<Record<string, StateDisplay>> = {
  queued: {
    label: 'Queued',
    tone: 'blue',
    explanation: 'Approved and waiting to be sent. Nothing has been sent yet.',
    effectConfirmed: false,
  },
  submitting: {
    label: 'Sending',
    tone: 'blue',
    explanation: 'The request is with the accounting package. The outcome is not known yet.',
    effectConfirmed: false,
  },
  confirmed: {
    label: 'Confirmed',
    tone: 'ok',
    explanation: 'The accounting package confirmed this, and the receipt below is the evidence.',
    effectConfirmed: true,
  },
  failed_safe: {
    label: 'Not applied',
    tone: 'warn',
    explanation: 'The accounting package proved nothing was applied, so this can be retried safely.',
    effectConfirmed: false,
    nextStep: 'authorise_retry',
  },
  unknown: {
    label: 'Outcome unknown',
    tone: 'warn',
    explanation:
      'We could not establish whether this was applied. It will not be sent again until a status check answers.',
    effectConfirmed: false,
    nextStep: 'check_status',
  },
  needs_review: {
    label: 'Needs review',
    tone: 'danger',
    // Two roads lead here and the label must not pick one: a status check that
    // established nothing, and an entry already present at the provider that we
    // did not create. The operation's error code says which. Not "duplicate",
    // because that would be wrong half the time; not "unknown", for the same
    // reason. Neither road says the ledger moved because of us.
    explanation:
      'Someone has to look at this. Either the status check could not establish what happened, or a matching entry was already in the accounting package.',
    effectConfirmed: false,
    nextStep: 'check_status',
  },
};

/** app.receipt_state. A draft is a draft; it is not a posting. */
export const RECEIPT_STATES: Readonly<Record<string, string>> = {
  draft_created: 'Draft created in the accounting package',
  posted: 'Posted in the accounting package',
  allocated: 'Allocated against the invoice',
  file_attached: 'File attached',
  import_accepted: 'Import accepted',
};

/** app.document_state. */
export const DOCUMENT_STATES: Readonly<Record<string, StateDisplay>> = {
  received: { label: 'Received', tone: 'neutral', explanation: 'Waiting to be read.', effectConfirmed: false },
  quarantined: {
    label: 'Quarantined',
    tone: 'danger',
    explanation: 'Held back before processing. See the blockers below.',
    effectConfirmed: false,
    nextStep: 'review',
  },
  extracting: { label: 'Being read', tone: 'neutral', explanation: 'Extraction is running.', effectConfirmed: false },
  needs_review: {
    label: 'Needs review',
    tone: 'warn',
    explanation: 'Something about this document stopped automatic preparation.',
    effectConfirmed: false,
    nextStep: 'review',
  },
  ready: {
    label: 'Ready for your decision',
    short: 'Ready',
    tone: 'ai',
    explanation: 'A proposal is prepared. Nothing is sent until you approve it.',
    effectConfirmed: false,
    nextStep: 'review',
  },
  approved: {
    label: 'Approved',
    tone: 'blue',
    // Approved is OUR state. It says a person decided, not that a ledger moved.
    explanation: 'You approved this. The external outcome is shown with the operation.',
    effectConfirmed: false,
  },
  excluded: {
    label: 'Excluded',
    tone: 'neutral',
    explanation: 'Excluded from the workflow. Nothing was deleted in the accounting package.',
    effectConfirmed: false,
  },
  // A bank statement whose lines are stored. Not a proposal and not a payment:
  // candidates wait in Bank matching for a person, and nothing went anywhere.
  imported: {
    label: 'Statement imported',
    tone: 'neutral',
    explanation:
      'Its lines are stored and any match candidates are waiting in Bank matching. Nothing has been sent to the accounting package.',
    effectConfirmed: false,
  },
};

/**
 * A confirmed booking, by what its receipt says. Keyed by app.receipt_state and
 * reachable ONLY through documentDisplay, which reads it only when the
 * operation itself is `confirmed` — so these are the two entries outside
 * OPERATION_STATES that may say the ledger moved, and a draft says it is one.
 */
export const BOOKING_RECEIPT_STATES: Readonly<Record<string, StateDisplay>> = {
  posted: {
    label: 'Booked',
    tone: 'ok',
    explanation: 'Booked: the accounting package confirmed it, and the receipt is with the operation.',
    effectConfirmed: true,
  },
  draft_created: {
    label: 'Draft in the accounting package',
    tone: 'ok',
    explanation: 'A draft exists in the accounting package. It is final only once someone finalises it there.',
    effectConfirmed: true,
  },
};

/**
 * What a document is, said once for every screen.
 *
 * Until approval the document's own state says it. After approval the decision
 * is made and the question is the ledger, which only the booking operation can
 * answer: queued and sending say so, unknown / not applied / needs review say
 * a person is needed, and only `confirmed` reaches "Booked" — and then only
 * with a `posted` receipt. A confirmed operation whose receipt is anything
 * else says "Confirmed", which is what is known.
 */
export function documentDisplay(state: string, booking: Booking | null | undefined): StateDisplay {
  if (state !== 'approved' || booking == null) return display(DOCUMENT_STATES, state);
  if (booking.state === 'confirmed') {
    const receipt = booking.receiptState === null ? undefined : BOOKING_RECEIPT_STATES[booking.receiptState];
    return receipt ?? display(OPERATION_STATES, 'confirmed');
  }
  return display(OPERATION_STATES, booking.state);
}

/**
 * app.match_status.
 *
 * `executed` is the one to be careful with. It means OUR record of the match
 * has been acted on, not that the provider allocated anything — the operation
 * and its receipt say that, and they say it separately. So its label speaks
 * about the match and points at the operation for the outcome, and its
 * effectConfirmed stays false like every other state outside OPERATION_STATES.
 */
export const MATCH_STATES: Readonly<Record<string, StateDisplay>> = {
  candidate: {
    label: 'Candidate',
    tone: 'ai',
    // ch.02: "Amount alone is insufficient: several invoices can share the same
    // total. Surface ambiguity." A candidate is evidence, not a recommendation.
    explanation: 'Evidence for you to judge. Nothing is sent until you approve it.',
    effectConfirmed: false,
    nextStep: 'review',
  },
  approved: {
    label: 'Approved',
    tone: 'blue',
    explanation: 'You approved this match. The external outcome is shown with the operation.',
    effectConfirmed: false,
  },
  executed: {
    label: 'Sent',
    tone: 'blue',
    explanation: 'The match has been acted on. Whether the ledger agrees is the operation below.',
    effectConfirmed: false,
  },
  stale: {
    label: 'Source changed',
    tone: 'warn',
    explanation: 'The bank line or the invoice moved since this candidate was computed.',
    effectConfirmed: false,
    nextStep: 'review',
  },
  rejected: { label: 'Rejected', tone: 'neutral', explanation: 'Not approved.', effectConfirmed: false },
};

/** app.proposal_status. */
export const PROPOSAL_STATES: Readonly<Record<string, StateDisplay>> = {
  draft: { label: 'Draft', tone: 'neutral', explanation: 'Not ready for a decision yet.', effectConfirmed: false },
  needs_review: {
    label: 'Needs review',
    tone: 'warn',
    explanation: 'A check did not pass. Correct it or exclude the document.',
    effectConfirmed: false,
    nextStep: 'review',
  },
  ready: {
    label: 'Ready for approval',
    tone: 'ai',
    explanation: 'Checks completed. Your approval is what sends it.',
    effectConfirmed: false,
    nextStep: 'review',
  },
  approved: {
    label: 'Approved',
    tone: 'blue',
    explanation: 'Approved. Watch the operation for the external outcome.',
    effectConfirmed: false,
  },
  stale: {
    label: 'Source changed',
    tone: 'warn',
    // ch.02: "Mark our outdated proposal stale, show the difference and require
    // a new decision."
    explanation: 'The ledger moved since this was prepared. It needs a fresh decision.',
    effectConfirmed: false,
    nextStep: 'review',
  },
  superseded: {
    label: 'Superseded',
    tone: 'neutral',
    explanation: 'A newer revision replaced this one.',
    effectConfirmed: false,
  },
  rejected: { label: 'Rejected', tone: 'neutral', explanation: 'Not approved.', effectConfirmed: false },
};

/**
 * What an audit action means, in English.
 *
 * Every one of these describes a DECISION or a REQUEST, never an outcome, and
 * that is the rule rather than an accident of wording. `app.audit_events` is
 * written in the same transaction as the decision it records, which is before
 * anything external has happened — so an event saying "approved" is true the
 * moment it is written, and an event saying "booked" would be a claim about a
 * provider nobody has called yet.
 *
 * Where an event points at an operation, the note says where the outcome
 * actually lives. The audit log is evidence that somebody decided something; it
 * is not a second place that reports whether the ledger moved.
 */
export interface AuditAction {
  readonly label: string;
  /** One clause of context, or undefined when the label says everything. */
  readonly note?: string;
}

export const AUDIT_ACTIONS: Readonly<Record<string, AuditAction>> = {
  'proposal.revised': {
    label: 'Proposal revised',
    note: 'A new immutable revision. Any earlier approval stopped being current.',
  },
  'proposal.approved': {
    label: 'Proposal approved',
    note: 'The decision was recorded and one operation was queued.',
  },
  'document.excluded': {
    label: 'Document excluded',
    note: 'Excluded from the workflow. Nothing was deleted in the accounting package.',
  },
  'bank_match.approved': {
    label: 'Bank match approved',
    note: 'The decision was recorded and one operation was queued.',
  },
  'operation.probe_requested': {
    label: 'Status check requested',
    note: 'Asking the provider what happened. No write was repeated.',
  },
  'operation.retry_authorised': {
    label: 'Retry authorised',
    note: 'Permitted only where the provider proved nothing had been applied.',
  },
  // --- the execution path. Everything below is written by the worker, with
  // --- the SYSTEM as its actor: nobody pressed anything.
  'operation.submitted': {
    // Not "sent": a request was made and this row cannot say whether it
    // arrived. That is the same distinction the state machine makes, and the
    // reason `submitting` exists as a state at all.
    label: 'Request made to the accounting package',
    note: 'Whether it landed is reported by the operation, not by this row.',
  },
  'operation.confirmed': {
    label: 'Confirmed by the accounting package',
    // The one action in this table that reports an external effect, and it is
    // still not where a reviewer should read the outcome from: a receipt can
    // say `draft_created`, and only the operation reports which.
    note: 'A receipt came back. The operation says whether it was a posting or a draft.',
  },
  'operation.failed_safe': {
    label: 'Stopped before anything changed',
    note: 'Nothing was sent, or the provider proved nothing was applied. Safe to look at without hurry.',
  },
  'operation.unknown': {
    label: 'Outcome not established',
    note: 'A request may have reached the accounting package. A status check has been queued; nothing was re-sent.',
  },
  'operation.unknown_after_crash': {
    label: 'Outcome not established after a restart',
    note: 'A worker stopped mid-request. Nothing is re-sent on this path; a status check decides.',
  },
  'operation.duplicate_present': {
    label: 'A matching entry was already there',
    // Deliberately says what WE did not do. A receipt is evidence that we
    // caused an effect; this is evidence that somebody else did, so none was
    // written and this row must not imply one.
    note: 'Something else had already created it. We created nothing and recorded no receipt.',
  },
  'operation.needs_review': {
    label: 'Waiting for a person',
    note: 'The accounting package could not prove what happened. Reconcile it by hand.',
  },
  'extraction.completed': {
    label: 'Proposal prepared',
    note: 'Prepared from the document. Nothing reaches the accounting package until somebody approves it.',
  },
  'extraction.blocked': {
    label: 'Preparation stopped on a finding',
    note: 'The document was read and something did not check out. The findings are on the document.',
  },
  'extraction.abandoned': {
    label: 'Preparation gave up',
    note: 'The document could not be read after repeated attempts. It is waiting for a person.',
  },
  'document.source_url_issued': {
    label: 'Source opened for viewing',
    note: 'A short-lived link to the stored bytes was issued to this person.',
  },
  'document.source_downloaded': {
    label: 'Source bytes read',
    note: 'The stored document was fetched through that link.',
  },
  'statement.imported': {
    label: 'Bank statement imported',
    note: 'The balances added up. Candidate matches, if any, are on Bank matching; nothing was sent to the accounting package.',
  },
  'statement.blocked': {
    label: 'Bank statement stopped',
    note: 'The file was read and did not check out — an unbalanced statement or an unlisted account. Nothing was stored.',
  },
  'statement.abandoned': {
    label: 'Bank statement import gave up',
    note: 'The statement could not be processed after repeated attempts. It is waiting for a person.',
  },
  'connection.sync_succeeded': {
    label: 'Synchronised with the accounting package',
    note: 'Reference data was read. Nothing was written.',
  },
  'connection.sync_failed': {
    label: 'Synchronisation failed',
    note: 'The accounting package could not be read. Connections shows the health.',
  },
  'counterparty.classified': {
    label: 'Counterparty classified',
    // What was decided, and what was NOT: no relation is created by this, in
    // either direction. Saying "a relation was created" would be false for both
    // answers — one-off books against the shared counterparty, and recurring
    // still waits for an approved mapping.
    note: 'A reviewer recorded whether this party is a one-off or a recurring supplier. No relation was created.',
  },
};

/** An action this build has never heard of is shown as itself, not guessed at. */
export function auditAction(action: string): AuditAction {
  return AUDIT_ACTIONS[action] ?? { label: action };
}

/** app.actor_kind. Who acted, in the vocabulary the database stores. */
export const ACTOR_KINDS: Readonly<Record<string, string>> = {
  user: 'Person',
  system: 'Automated',
  agency_operator: 'Agency operator',
};

const UNKNOWN_STATE: StateDisplay = {
  label: 'Unrecognised state',
  tone: 'warn',
  // A state this build has never heard of is not a reason to guess. Showing the
  // canonical value is more useful to whoever has to explain it than a label
  // invented here.
  explanation: 'This build does not recognise the state reported by the server.',
  effectConfirmed: false,
  nextStep: 'review',
};

export function display(table: Readonly<Record<string, StateDisplay>>, state: string): StateDisplay {
  return table[state] ?? { ...UNKNOWN_STATE, label: state };
}

/** Blocker codes the API can report today, from stored evidence only. */
export const BLOCKER_LABELS: Readonly<Record<string, string>> = {
  quarantined: 'Held back before processing',
  proposal_stale: 'The ledger changed after this proposal was prepared',
  // The two findings the counterparty classification can produce. Both are
  // questions for a person, and neither says a relation was or will be created:
  // the whole point of the classification is that nothing permanent is written
  // on a guess. See packages/domain/counterparty.ts.
  COUNTERPARTY_UNCLASSIFIED:
    'Classify this party: a recurring business supplier, or a one-off purchase',
  ONE_OFF_RELATION_NOT_CONFIGURED:
    'This administration has no one-off counterparty configured to book against',
  SOURCE_UNREADABLE: 'The document could not be read',
  STATEMENT_UNBALANCED: 'The statement does not add up: opening balance plus its lines is not the closing balance. Nothing was imported',
  MT940_UNPARSEABLE: 'This is not a statement that could be read',
  MT940_NO_STATEMENTS: 'The file contains no statement',
  BANK_ACCOUNT_NOT_CONFIGURED:
    'This account is not one of this administration\'s bank accounts. Nothing was imported — add the account if it is right',
  BANK_ACCOUNT_NOT_IDENTIFIED: 'The statement does not name an IBAN this administration could be checked against',
  NO_EXTERNAL_MAPPING: 'This administration is not connected to its accounting package',
  IMPORT_ABANDONED: 'The import was tried several times and could not finish',
  OCR_LOW_CONFIDENCE:
    'The text read from this image is too uncertain to prepare a booking from. Nothing was sent to the model',
  // packages/domain/vat-number.ts. Never corrected for the reader: which
  // character is wrong is not something a format can tell.
  VAT_NUMBER_MALFORMED: 'VAT number looks misread',
  VAT_NUMBER_CHECKSUM: "Checksum doesn't add up, check this number",
  RECEIPT_NOT_POSTABLE: "Receipts can't be posted to e-Boekhouden yet",
  GATEWAY_ROUTE_UNCONFIRMED: 'Where the model processed this could not be confirmed, so its answer was not used',
  HELD_ANSWER_TEXT_CHANGED: 'The document reads differently now than when it was sent, so the held answer was not used. Read it again',
  GATEWAY_ROUTE_CHANGED_WHILE_PENDING: 'The approved model route changed while the answer was held, so it was not used. Read it again',
  // Receipts and credit notes (refinement 6).
  CREDIT_NOTE_NOT_POSTABLE: "Credit notes can't be posted to e-Boekhouden yet",
  CREDIT_NOTE_SIGNS_MIXED: 'Some amounts are printed negative and some positive; a credit note is one or the other',
  PAYMENT_METHOD_UNKNOWN: 'The receipt does not say how it was paid: choose cash or card/bank',
  CARD_RECEIPT_LIVE_FEED:
    "Paid by card in an administration with a live bank feed: book it via the bank line in e-Boekhouden, so it isn't booked twice.",
  CASH_LEDGER_NOT_CONFIGURED: 'This administration has no cash ledger configured to book a cash receipt from',
  BANK_LEDGER_NOT_CONFIGURED: 'This administration has no bank ledger configured to book a card receipt from',
  RECEIPT_BANK_LINE_NOT_FOUND:
    'No imported bank statement line matches this card payment yet. It is read again when the next statement is imported',
  RECEIPT_BANK_LINE_AMBIGUOUS: 'Several imported bank statement lines match this card payment; it cannot be told which',
  RECEIPT_JOURNAL_NOT_CONFIGURED: 'This administration has no journal configured for receipts',
  RECEIPT_AMOUNT_NOT_POSITIVE: 'A receipt total must be positive',
  PAYMENT_LEDGER_UNKNOWN: "The configured cash or bank ledger is not in the package's chart",
};

/**
 * Why a document could not be read: the reader's and the store's own codes.
 * An unknown code is shown as itself, never as a friendlier guess.
 */
export const REASON_LABELS: Readonly<Record<string, string>> = {
  NO_TEXT_LAYER: 'This PDF has no text layer — it is a scan, and needs OCR',
  NO_TEXT_FOUND: 'OCR found no text in it',
  IMAGE_UNREADABLE: 'The image could not be decoded',
  OPEN_ITEMS_UNAVAILABLE: 'The accounting package did not list its open items',
  NO_TEXT_ROUTE: 'There is no reader for this file type yet',
  PDF_UNREADABLE: 'The PDF could not be parsed',
  READER_TIMEOUT: 'Reading took too long and was stopped',
  READER_LIMIT_EXCEEDED: 'The file needed more memory or time than a document should',
  READER_OUTPUT_TOO_LARGE: 'The file produced far more text than a document has',
  CONTENT_HASH_MISMATCH: 'The stored file does not match the version that was received',
  ROUTE_NOT_REPORTED: 'the provider never reported the region',
  REGION_NOT_APPROVED: 'the provider reported a region outside the approval',
  SERVED_BY_NOT_APPROVED: 'the provider reported a server outside the approval',
  MODEL_NOT_APPROVED: 'a different model answered than the one approved',
};

export function blockerLabel(code: string, reason?: string): string {
  const label = BLOCKER_LABELS[code] ?? code;
  return reason === undefined ? label : `${label}: ${REASON_LABELS[reason] ?? reason}`;
}

/** How the text was obtained, in a reviewer's words. */
export const TEXT_ROUTE_LABELS: Readonly<Record<string, string>> = {
  plain_text: 'Plain-text file, read as is',
  pdf_text_layer: "Text layer embedded in the PDF (no OCR)",
  ocr_local: 'OCR of the image, on this server',
};

/**
 * app.request_state — the nine ch.02 names, and what each one does NOT claim.
 *
 * ch.02: "Use explicit states draft, approved, sending, sent, delivery_unknown,
 * waiting, reply_received, resolved, cancelled." Two of them are the reason
 * this table exists rather than a switch in the screen:
 *
 *   * `approved` is OUR state. A reviewer decided; a send operation was queued.
 *     Nothing has been sent, and in this build nothing will be — the operation
 *     carries that, and the screen prints the operation beside the state.
 *   * `reply_received` says a document came back, not that the missing evidence
 *     is complete. ch.02: "Receiving a reply does not approve a booking or
 *     prove the missing evidence is complete."
 *
 * Every label here is about the REQUEST. None of them says a message was
 * delivered, because delivery is a mail event and mail events are separate rows
 * for exactly that reason — ch.02: "Store provider acceptance and later
 * bounce/failure separately."
 */
export const REQUEST_STATES: Readonly<Record<string, StateDisplay>> = {
  draft: {
    label: 'Draft',
    tone: 'neutral',
    explanation: 'Nobody has approved this yet, and nothing has been sent.',
    effectConfirmed: false,
    nextStep: 'review',
  },
  approved: {
    label: 'Approved to send',
    tone: 'blue',
    explanation: 'You approved the exact message and recipient. Whether it left is the operation below.',
    effectConfirmed: false,
  },
  sending: {
    label: 'Sending',
    tone: 'blue',
    explanation: 'Handed to the mail transport. The outcome is not known yet.',
    effectConfirmed: false,
  },
  sent: {
    label: 'Sent',
    tone: 'blue',
    explanation: 'The mail transport accepted it. A bounce can still arrive, and would be listed separately.',
    effectConfirmed: false,
  },
  delivery_unknown: {
    label: 'Delivery unknown',
    tone: 'warn',
    explanation: 'We could not establish whether this reached the customer. It will not be sent again on its own.',
    effectConfirmed: false,
    nextStep: 'check_status',
  },
  waiting: {
    label: 'Waiting for a reply',
    tone: 'neutral',
    explanation: 'Asked, and nothing has come back yet.',
    effectConfirmed: false,
  },
  reply_received: {
    label: 'Reply received',
    tone: 'ai',
    explanation:
      'Something came back and is attached. That is not evidence the missing document arrived — somebody has to look.',
    effectConfirmed: false,
    nextStep: 'review',
  },
  resolved: {
    label: 'Resolved',
    tone: 'ok',
    explanation: 'Closed. Nothing further is expected from the customer.',
    effectConfirmed: false,
  },
  cancelled: {
    label: 'Cancelled',
    tone: 'neutral',
    explanation: 'Withdrawn. Anything already sent cannot be recalled.',
    effectConfirmed: false,
  },
};

/** app.mail_event_kind, as one phrase each. */
export const MAIL_EVENTS: Readonly<Record<string, string>> = {
  accepted: 'The mail transport accepted it',
  delivered: 'The receiving server accepted it',
  bounced: 'It bounced',
  failed: 'Sending failed',
  reply_received: 'A reply was attached',
};

// ---------------------------------------------------------------------------
// The same tables, in the reader's language.
//
// Everything above stays English and stays the reference: its shapes, its
// rules and its tests are the contract. The translations live in the message
// catalogues (i18n/states.en.ts, i18n/states.nl.ts) under `state.*` keys that
// mirror these tables entry for entry, and the helpers below lay them over the
// English entry — label, short and explanation only. Tone, effectConfirmed and
// nextStep are never translated, because they are not words: they are the
// claim, and the claim is the same in every language.
//
// A state with no key falls back to the English entry above, and a state the
// table does not know falls back exactly as display() does. So an untranslated
// state reads in English rather than as a raw key, and nothing here can make a
// state say more than the English table lets it.
// ---------------------------------------------------------------------------

type TableName = 'operation' | 'document' | 'bookingReceipt' | 'match' | 'proposal' | 'request';

const TABLES_BY_NAME: readonly (readonly [TableName, Readonly<Record<string, StateDisplay>>])[] = [
  ['operation', OPERATION_STATES],
  ['document', DOCUMENT_STATES],
  ['bookingReceipt', BOOKING_RECEIPT_STATES],
  ['match', MATCH_STATES],
  ['proposal', PROPOSAL_STATES],
  ['request', REQUEST_STATES],
];

/**
 * Where each entry came from, by identity. display() and documentDisplay()
 * return the table entries themselves, so an already-resolved StateDisplay
 * (StateBadge's `shown`) can be traced back to its table and state.
 */
const ORIGIN = new Map<StateDisplay, readonly [TableName, string]>(
  TABLES_BY_NAME.flatMap(([name, table]) =>
    Object.entries(table).map(([state, shown]) => [shown, [name, state]] as const),
  ),
);

/** The message for a `state.*` key, or undefined when the catalogue has none. */
function message(t: Translate, key: string): string | undefined {
  return Object.hasOwn(en, key) ? t(key as MessageKey) : undefined;
}

/** A StateDisplay from the tables above, with its words in the reader's language. */
function localiseShown(t: Translate, shown: StateDisplay): StateDisplay {
  const origin = ORIGIN.get(shown);
  if (origin === undefined) {
    // Not a table entry: the unknown fallback (label = canonical value, kept)
    // or something a caller built itself (left alone).
    return shown.explanation === UNKNOWN_STATE.explanation
      ? { ...shown, explanation: message(t, 'state.unknown.explanation') ?? shown.explanation }
      : shown;
  }
  const base = `state.${origin[0]}.${origin[1]}`;
  const short = shown.short === undefined ? undefined : (message(t, `${base}.short`) ?? shown.short);
  return {
    ...shown,
    label: message(t, `${base}.label`) ?? shown.label,
    explanation: message(t, `${base}.explanation`) ?? shown.explanation,
    ...(short === undefined ? {} : { short }),
  };
}

/** The state tables and their helpers, in one language. Same shapes as above. */
export interface LocalStates {
  /** display(), localised. */
  readonly display: (table: Readonly<Record<string, StateDisplay>>, state: string) => StateDisplay;
  /** documentDisplay(), localised. */
  readonly documentDisplay: (state: string, booking: Booking | null | undefined) => StateDisplay;
  /** An entry already resolved from one of the tables (e.g. by documentDisplay), localised. */
  readonly shown: (shown: StateDisplay) => StateDisplay;
  /** RECEIPT_STATES[state] ?? state, localised. */
  readonly receipt: (state: string) => string;
  /** auditAction(), localised. */
  readonly auditAction: (action: string) => AuditAction;
  /** ACTOR_KINDS[kind] ?? kind, localised. */
  readonly actorKind: (kind: string) => string;
  /** blockerLabel(), localised. */
  readonly blockerLabel: (code: string, reason?: string) => string;
  /** TEXT_ROUTE_LABELS[route] ?? route, localised. */
  readonly textRoute: (route: string) => string;
  /** MAIL_EVENTS[kind] ?? kind, localised. */
  readonly mailEvent: (kind: string) => string;
  /** An operation name ("create_purchase_entry"), as words. */
  readonly operationName: (name: string) => string;
}

function phrase(t: Translate, prefix: string, table: Readonly<Record<string, string>>, code: string): string {
  return (Object.hasOwn(table, code) ? message(t, `state.${prefix}.${code}`) : undefined) ?? table[code] ?? code;
}

/** The tables in the language `t` speaks. For a component, useStates() is the same thing. */
export function localStates(t: Translate): LocalStates {
  return {
    display: (table, state) => localiseShown(t, display(table, state)),
    documentDisplay: (state, booking) => localiseShown(t, documentDisplay(state, booking)),
    shown: (shown) => localiseShown(t, shown),
    receipt: (state) => phrase(t, 'receipt', RECEIPT_STATES, state),
    auditAction: (action) => {
      const english = auditAction(action);
      if (!Object.hasOwn(AUDIT_ACTIONS, action)) return english;
      const label = message(t, `state.audit.${action}.label`) ?? english.label;
      const note = english.note === undefined ? undefined : (message(t, `state.audit.${action}.note`) ?? english.note);
      return note === undefined ? { label } : { label, note };
    },
    actorKind: (kind) => phrase(t, 'actor', ACTOR_KINDS, kind),
    blockerLabel: (code, reason) => {
      const label = phrase(t, 'blocker', BLOCKER_LABELS, code);
      return reason === undefined ? label : `${label}: ${phrase(t, 'reason', REASON_LABELS, reason)}`;
    },
    textRoute: (route) => phrase(t, 'textRoute', TEXT_ROUTE_LABELS, route),
    mailEvent: (kind) => phrase(t, 'mail', MAIL_EVENTS, kind),
    operationName: (name) => message(t, `state.operationName.${name}`) ?? name.replace(/_/g, ' '),
  };
}

/** localStates() in the current interface language. */
export function useStates(): LocalStates {
  const t = useT();
  return useMemo(() => localStates(t), [t]);
}
